import React, { useEffect, lazy, Suspense } from 'react';

import { Helmet } from 'react-helmet-async';
import {
  useParams,
  useNavigate,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import classNames from 'classnames';
import NotFound from '../../components/NotFound';
import UnderMaintenance from '../../components/UnderMaintenance';
import Discover from '../../components/Dashboard/Discover';
import Chat from '../../components/Dashboard/Chat';
import Dashboard from '../../components/Dashboard/Dashboard';
import Candidates from '../../components/Dashboard/Candidates';
import Companies from '../../components/Dashboard/Companies';
import Jobs from '../../components/Dashboard/Jobs';
import Console from '../../components/Dashboard/Console';
import Header from '../../components/Dashboard/Header';
import SideMenu from '../../components/Dashboard/SideMenu';
import Calendar from '../../components/Dashboard/Calendar';
import Search from '../../components/Dashboard/Search';
import IntelSearch from '../../components/Dashboard/IntelSearch';
import ProfileSettings from '../../components/Dashboard/ProfileSettings';
import Reports from '../../components/Dashboard/Reports';
import FeedWatcher from '../../components/Dashboard/Discover/FeedWatcher';
import InterviewDashboard from '../../components/Dashboard/InterviewDashboard';
import CompanyDashboard from '../../components/Dashboard/CompanyDashboard';
import { useUserStore } from '../../store';
import { isPageAllowed, getRoleHomePath } from '../../helpers/pageAccess';
import classes from './styles.module.scss';

const RestaurantsMap = lazy(() =>
  import('../../components/Dashboard/RestaurantsMap')
);

export default function DashboardPage() {
  const { page, '*': thirdLevelPage } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { pathname } = location;

  const isTabletOrMobile = useMediaQuery({
    query: pathname.includes('discover')
      ? '(max-width: 1224px)'
      : '(max-width: 1024px)',
  });

  const { firstAvailableLink, userRoles } = useUserStore();

  const { t } = useTranslation();

  useEffect(() => {
    if (firstAvailableLink && pathname === '/tools') {
      navigate(firstAvailableLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstAvailableLink, pathname]);

  const renderPage = () => {
    // Role-based access control. The SideMenu only hides links a role may not
    // use; this enforces the same rules for direct URL access. Only enforce
    // once roles are known so we don't redirect during the initial load.
    if (userRoles.length > 0 && !isPageAllowed(page, userRoles)) {
      return (
        <Navigate to={getRoleHomePath(userRoles, firstAvailableLink)} replace />
      );
    }

    if (
      thirdLevelPage === '' &&
      page !== 'discover' &&
      page !== 'jobs' &&
      page !== 'admin' &&
      page !== 'settings'
    ) {
      return <NotFound />;
    }

    if (thirdLevelPage && thirdLevelPage.includes('/')) {
      return <NotFound />;
    }

    switch (page) {
      case 'discover':
        return <Discover />;
      case 'dashboard':
        return <Dashboard />;
      case 'jobs':
        return <Jobs />;
      case 'candidates':
        return <Candidates />;
      case 'companies':
        return <Companies />;
      case 'calendar':
        return <Calendar />;
      case 'reports':
        return <Reports />;
      case 'intel-search':
        return <IntelSearch />;
      case 'admin':
        return <Console />;
      case 'interview-dashboard':
        return <InterviewDashboard />;
      case 'company-dashboard':
        return <CompanyDashboard />;
      case 'settings':
        return <ProfileSettings />;
      case 'search':
        return <Search />;
      case 'chat':
        return <Chat />;
      case 'map':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <RestaurantsMap />
          </Suspense>
        );
      case '502':
        return <UnderMaintenance />;
      case '404':
      default:
        return <NotFound />;
    }
  };

  return (
    <div
      className={classNames(classes.DashboardPage, {
        [classes.mobile]: isTabletOrMobile,
      })}
    >
      <Helmet>
        <title>{t(`common.dashboardSections.${page}`)}</title>
      </Helmet>
      <SideMenu />
      <div className={classes.container} id="dashboardPageContainer">
        <Header />
        <FeedWatcher />
        {renderPage()}
      </div>
    </div>
  );
}
